import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star, Award, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, services, brandsRappresentanza, brandsDistribuzione } from '../utils/mockData';

const Home = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    // Check screen size
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Show video after 5 seconds if reduced motion is not preferred
    const timer = setTimeout(() => {
      if (!mediaQuery.matches) {
        setShowVideo(true);
      }
    }, 5000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const stats = [
    { icon: Users, value: "1500+", label: "Clienti Soddisfatti" },
    { icon: Award, value: "25+", label: "Anni di Esperienza" },
    { icon: Star, value: "14", label: "Marchi Partner" },
    { icon: TrendingUp, value: "100%", label: "Qualità Garantita" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_edil-service/artifacts/2ayg28e1_5f47bcf5-11d5-4d7b-8422-5f5fccd9c2d4.jpeg"
            alt="Edil Service 2.0 - Struttura Aziendale"
            className="w-full h-full object-cover"
            style={{
              opacity: showVideo ? 0 : 1,
              transition: 'opacity 1.5s ease-in-out'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-800/85 to-slate-900/70"></div>
        </div>

        {/* Video Background (appears after 5 seconds) */}
        {!prefersReducedMotion && (
          <div 
            className="absolute inset-0 z-0"
            style={{
              opacity: showVideo ? 1 : 0,
              transition: 'opacity 1.5s ease-in-out'
            }}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
              style={{ display: showVideo ? 'block' : 'none' }}
            >
              <source 
                src={isMobile ? "/media/edilservice-video-home-mobile.mp4" : "/media/edilservice-video-home-desktop.mp4"} 
                type="video/mp4" 
              />
            </video>
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-800/85 to-slate-900/70"></div>
          </div>
        )}

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block mb-6">
              <span className="bg-orange-600/20 text-orange-400 px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm border border-orange-500/30">
                ✨ {companyInfo.slogan}
              </span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'EB Garamond', serif" }}>
              Edil Service 2.0 SRL
            </h1>
            
            <p className="text-2xl md:text-3xl text-slate-200 mb-8 leading-relaxed" style={{ fontFamily: "'EB Garamond', serif" }}>
              Materiali e Tecnologie Avanzate per l'Edilizia
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link to="/contatti">
                <Button size="lg" className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white text-lg px-8 py-6 shadow-xl shadow-orange-500/30 group">
                  Contattaci Ora
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </Link>
              <Link to="/prodotti">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-800 text-lg px-8 py-6 backdrop-blur-sm">
                  Scopri i Prodotti
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-6 text-slate-300">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                <span>Prodotti Certificati</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                <span>Consegna Rapida</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                <span>Assistenza Dedicata</span>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10"></div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="text-center border-none shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-slate-50 to-white">
                <CardContent className="pt-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white mb-4">
                    <stat.icon size={28} />
                  </div>
                  <h3 className="text-3xl font-bold text-slate-800 mb-2">{stat.value}</h3>
                  <p className="text-slate-600 font-medium">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Scopri la Nostra Struttura
            </h2>
            <p className="text-xl text-slate-300">
              Un'azienda moderna al servizio dei professionisti dell'edilizia
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full h-auto"
              >
                <source src="https://customer-assets.emergentagent.com/job_edil-service/artifacts/7efsvtww_a5e94dcd-4de2-4bbc-8194-1c86991699ba.mp4" type="video/mp4" />
                Il tuo browser non supporta il tag video.
              </video>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-6">
                <p className="text-white text-lg font-semibold">
                  📍 Contrada Pezza La Pera di Sopra, San Ferdinando di Puglia (BT)
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              I Nostri Servizi
            </h2>
            <p className="text-xl text-slate-600">
              Soluzioni complete per professionisti e privati
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 border-none overflow-hidden">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform">
                    {/* Icon placeholder - will be replaced with actual icons */}
                    <div className="text-2xl font-bold">{service.id}</div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-orange-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/servizi">
              <Button size="lg" className="bg-slate-800 hover:bg-slate-900 text-white">
                Vedi Tutti i Servizi
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              I Nostri Marchi Partner
            </h2>
            <p className="text-xl text-slate-600">
              Rappresentanza esclusiva e distribuzione autorizzata dei migliori brand del settore
            </p>
          </div>

          {/* Rappresentanza */}
          <div className="mb-16">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="h-px bg-gradient-to-r from-transparent via-blue-600 to-transparent flex-1 max-w-xs"></div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-blue-600 mb-2">Marchi in Rappresentanza</h3>
                <p className="text-sm text-slate-600">Con assistenza tecnica specializzata di Francesco Valerio (30 anni di esperienza)</p>
              </div>
              <div className="h-px bg-gradient-to-r from-transparent via-blue-600 to-transparent flex-1 max-w-xs"></div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {brandsRappresentanza.map((brand, index) => (
                <div 
                  key={index}
                  className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl hover:shadow-xl transition-all duration-300 border-2 border-blue-200 hover:border-blue-400 group min-h-[140px]"
                >
                  <div className="w-full h-16 flex items-center justify-center mb-3 px-2">
                    <img 
                      src={brand.logo} 
                      alt={`${brand.name} logo`}
                      className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://via.placeholder.com/200x80/f1f5f9/334155?text=${brand.name}`;
                      }}
                    />
                  </div>
                  <div className="text-blue-700 font-bold text-sm text-center">{brand.name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Distribuzione */}
          <div>
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="h-px bg-gradient-to-r from-transparent via-slate-400 to-transparent flex-1 max-w-xs"></div>
              <h3 className="text-2xl font-bold text-slate-700">Marchi in Distribuzione</h3>
              <div className="h-px bg-gradient-to-r from-transparent via-slate-400 to-transparent flex-1 max-w-xs"></div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {brandsDistribuzione.map((brand, index) => (
                <div 
                  key={index}
                  className="flex flex-col items-center justify-center p-4 bg-white rounded-xl hover:shadow-xl transition-all duration-300 border border-slate-200 group min-h-[120px]"
                >
                  <div className="w-full h-16 flex items-center justify-center mb-2 px-2">
                    <img 
                      src={brand.logo} 
                      alt={`${brand.name} logo`}
                      className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://via.placeholder.com/200x80/f1f5f9/334155?text=${brand.name}`;
                      }}
                    />
                  </div>
                  <div className="text-slate-600 font-medium text-xs text-center leading-tight">{brand.name}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/marchi">
              <Button size="lg" variant="outline" className="border-2 border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white">
                Scopri Tutti i Marchi
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Pronto a Iniziare il Tuo Progetto?
            </h2>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed">
              Contattaci oggi stesso per un preventivo personalizzato. Il nostro team di esperti è pronto ad assisterti.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contatti">
                <Button size="lg" className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white text-lg px-10 py-6 shadow-xl shadow-orange-500/30">
                  Richiedi Preventivo Gratuito
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-800 text-lg px-10 py-6">
                  Chiamaci: {companyInfo.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
